import { Button, Card, CardContent, Typography } from "@mui/material";
import { useGetChannelConnections } from "hooks/useGetChannelConnections";
import { FaCircle, FaInstagram, FaTelegram, FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CHANNEL_STATUS } from "../Whatsapp/WhatsappConnect";
import { MdAdd, MdScience } from "react-icons/md";
import { useState } from "react";
import { useSelector } from "react-redux";
import { getAgents } from "redux/selectors/getAgents";
import CreateChannelDialog from "./CreateChannelDialog";
import PageGuide from "components/PageGuide/PageGuide";
import { CHANNEL_LABELS } from "components/PageGuide/channelGuides";
import { canDeleteChannel, useDeleteChannel } from "./useDeleteChannel";

const SOURCE_ICONS: Record<string, JSX.Element> = {
  WHATSAPP: <FaWhatsapp />,
  TELEGRAM: <FaTelegram />,
  INSTAGRAM: <FaInstagram />,
  TEST_SANDBOX: <MdScience />,
};

const Channels = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: channelConnections, refetch } = useGetChannelConnections({});
  const agents = ((useSelector(getAgents) as any[]) || []) as any[];
  const agentsByID = new Map<number, any>(agents.map((a: any) => [a.id, a]));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const deleteChannel = useDeleteChannel();

  return (
    <div>
      <div className="mb-16 flex items-start justify-between">
        <div>
          <h3 className="mb-2">Каналы</h3>
          <p>
            Канал — это мессенджер (Instagram, WhatsApp, Telegram), откуда приходят сообщения клиентов и
            куда отвечает ИИ-агент. Один канал привязывается к одному агенту.
          </p>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<MdAdd />}
          onClick={() => setIsDialogOpen(true)}
        >
          Добавить канал
        </Button>
      </div>

      <PageGuide id="channels" title="Как подключить мессенджер">
        <ol className="list-decimal pl-5 flex flex-col gap-1">
          <li>Нажмите «Добавить канал», выберите тип (Instagram, WhatsApp, Telegram) и ИИ-агента, который будет отвечать.</li>
          <li>Откроется страница канала с пошаговой инструкцией для выбранного мессенджера.</li>
          <li>Когда статус станет «Подключено», сообщения клиентов появятся во «Входящих», а агент начнёт отвечать.</li>
          <li>Хотите сначала проверить агента? Используйте «Тестовую среду» — она не требует мессенджеров и не входит в лимит каналов.</li>
        </ol>
      </PageGuide>

      {channelConnections?.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" className="text-center">
              Нет подключённых каналов. Нажмите «Добавить канал», чтобы создать первый.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
          {channelConnections?.map((connection: any) => {
            const agent = agentsByID.get(connection.agentID);
            return (
              <Card key={connection.id}>
                <CardContent>
                  <div className="flex items-center w-full mb-4">
                    <div className="flex justify-center items-center mr-2 text-2xl">
                      {SOURCE_ICONS[connection.source] || <MdAdd />}
                    </div>
                    <Typography variant="body1" className="flex-grow">
                      {CHANNEL_LABELS[connection.source] || connection.source}
                    </Typography>
                    {connection.status === CHANNEL_STATUS.ACTIVE ? (
                      <FaCircle className="text-green-500 ml-2" size={12} />
                    ) : (
                      <FaCircle className="text-orange-500 ml-2" size={12} />
                    )}
                  </div>
                  <div className="mb-3 text-sm text-gray-600">
                    <div>
                      Агент: {connection.agentID ? agent?.name || `#${connection.agentID}` : "—"}
                    </div>
                    {connection.externalAccountUserName && (
                      <div>Аккаунт: {connection.externalAccountUserName}</div>
                    )}
                    {connection.connectionStatus && (
                      <div>Статус: {t(`CONNECTION_STATUS.${connection.connectionStatus}`)}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      fullWidth
                      variant="text"
                      color="primary"
                      onClick={() => navigate(`/admin/channels/${connection.id}`)}
                    >
                      Детали
                    </Button>
                    {canDeleteChannel(connection) && (
                      <Button
                        fullWidth
                        variant="text"
                        color="error"
                        onClick={() => deleteChannel(connection)}
                      >
                        Удалить
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateChannelDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCreated={(channelConnectionID) => {
          setIsDialogOpen(false);
          refetch();
          navigate(`/admin/channels/${channelConnectionID}`);
        }}
      />
    </div>
  );
};

export default Channels;
